import PriceDetailCard from "./PriceDetailCard";
import StyledButton from "@/components/styled/StyledButton";
import { RotateCcw } from "lucide-react";
import { FC, useEffect, useRef, useState } from "react";
import { useMediaQuery } from "@mui/material";
import { useDebounce } from "@/hooks/useDebounce";

interface PriceDetailCardDesktopProps {
  onSubmit: () => void;
  reset: () => void;
  showWinePrice: boolean;
}

const PriceDetailCardDesktop: FC<PriceDetailCardDesktopProps> = ({
  onSubmit,
  reset,
  showWinePrice,
}) => {
  const [topPosition, setTopPosition] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);
  const scrollContainerRef = useRef<HTMLElement | null>(null);
  const isXl = useMediaQuery("(min-width: 1280px)");
  const value = useDebounce(topPosition, 200);

  useEffect(() => {
    const findScrollContainer = () => {
      if (containerRef.current) {
        let element = containerRef.current.parentElement;
        while (element) {
          const computedStyle = window.getComputedStyle(element);
          if (computedStyle.overflow === "auto" || computedStyle.overflowY === "auto") {
            return element;
          }
          element = element.parentElement;
        }
      }
      return null;
    };

    scrollContainerRef.current = findScrollContainer();

    const handleScroll = () => {
      if (scrollContainerRef.current && containerRef.current) {
        let parentContainer = containerRef.current.parentElement;
        while (parentContainer && !parentContainer.className.includes("md:gap-14")) {
          parentContainer = parentContainer.parentElement;
        }

        if (parentContainer) {
          const parentRect = parentContainer.getBoundingClientRect();
          const scrollY = window.scrollY;
          const parentTop = parentRect.top + scrollY;

          const newTopPosition = Math.max(0, scrollY - parentTop + 8);

          setTopPosition(newTopPosition);
        }
      }
    };

    if (scrollContainerRef.current) {
      // Add scroll event listener to the scrollable container
      scrollContainerRef.current.addEventListener("scroll", handleScroll, {
        passive: true,
      });

      // Initial calculation
      handleScroll();
    }

    // Cleanup
    return () => {
      if (scrollContainerRef.current) {
        scrollContainerRef.current.removeEventListener("scroll", handleScroll);
      }
    };
  }, []);

  return (
    <div
      ref={containerRef}
      className="relative hidden flex-1 flex-col gap-6 md:flex"
      style={isXl ? { top: `${value}px` } : undefined}
    >
      <div className={"flex rounded-xl border-2 border-gray-200 bg-white shadow-md"}>
        <PriceDetailCard showWinePrice={showWinePrice} />
      </div>
      <div className="flex flex-col gap-4">
        <StyledButton mode="primary" onClick={onSubmit}>
          Enregistrer
        </StyledButton>
        <StyledButton
          mode="secondary"
          onClick={reset}
          className="group flex flex-row justify-center gap-2"
        >
          <RotateCcw className="text-blue-600 transition duration-700 ease-in-out group-hover:-rotate-180" />
          Réinitialiser
        </StyledButton>
      </div>
    </div>
  );
};

export default PriceDetailCardDesktop;
